import * as WebBrowser from "expo-web-browser";
import { observer } from "mobx-react";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Image, Platform, StyleSheet } from "react-native";
import { EvidensiaLogo } from "~/assets/logos/EvidensiaLogo";
import { Layout } from "~/components/Layout";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { BodyText } from "~/components/V2/Text/BodyText";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { withAuthData } from "~/hoc/withAuthData";
import { useResponsive } from "~/hooks/useResponsive";
import { BankIdLoginButton } from "./BankIdLoginButton";
import type * as AuthSession from "expo-auth-session";

const styles = StyleSheet.create({
  bottomView: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    marginTop: -40,
    paddingBottom: 72,
    paddingTop: 44,
  },
  imageWrapper: {
    position: "absolute",
    width: "100%",
    height: "100%",
  },
  image: { width: "100%", height: "100%" },
});

WebBrowser.maybeCompleteAuthSession();

interface LoginScreenWithAuthDataProps {
  authRequest: AuthSession.AuthRequest;
  discovery: AuthSession.DiscoveryDocument;
}

export const LoginScreenWithAuthData = withAuthData(
  observer(function LoginScreenWithAuthData({
    discovery,
    authRequest,
  }: LoginScreenWithAuthDataProps) {
    const errorAlert = useErrorAlertV2();
    const r = useResponsive();

    return (
      <Screen withBottomInset withTopInset={false} preventScroll>
        <Layout cardStyle={{ maxHeight: 780 }}>
          {/* Image */}
          <View flex style={styles.imageWrapper}>
            <View flex>
              <Image
                source={r(
                  require("./assets/login-photo.jpg"),
                  require("./assets/login-photo.jpg"),
                  require("./assets/login-photo-02.jpg"),
                )}
                style={styles.image}
                resizeMode="cover"
              />
            </View>
            {/* View used just for layouting so that photo looks better */}
            <View
              style={{
                height: r(200, 200, 160),
                width: "100%",
              }}
            />
          </View>

          {/* View used just for layouting */}
          <View flex />
          {/* End Image */}
          {/* Bottom Sheet */}
          <View
            centerContent
            colorPrimary
            style={[
              styles.bottomView,
              {
                paddingHorizontal: 24,
                minWidth: r(undefined, undefined, 615),
              },
            ]}
          >
            <EvidensiaLogo size={85} />
            <Spacer extraExtraLarge />

            <BodyText sizeLarge weightRegular colorWhite>
              <FormattedMessage
                defaultMessage="Login"
                id="TRDtyo"
                description="Text above login button"
              />
            </BodyText>
            <Spacer large />
            <BankIdLoginButton
              discovery={discovery}
              authRequest={authRequest}
              onError={(error) => {
                if (error === "DUPLICATE_USER_FOUND") {
                  errorAlert(
                    new Error("Duplicate user found"),
                    intl.formatMessage({
                      id: "rIfma1",
                      defaultMessage: "Login failed",
                      description:
                        "Title for error message when duplicate user is found",
                    }),
                    intl.formatMessage({
                      id: "/yc/w7",
                      defaultMessage:
                        "We have detected that multiple profiles are linked to your personal identification number, which prevents you from booking visits online.",
                      description:
                        "Description for error message when duplicate user is found",
                    }),
                    intl.formatMessage({
                      id: "Hp/8ql",
                      defaultMessage: "Ok close",
                      description: "Button text for error message",
                    }),
                    undefined,
                    true,
                  );
                } else if (error === "USER_NOT_FOUND") {
                  errorAlert(
                    new Error("User not found"),
                    intl.formatMessage({
                      id: "cvhTTu",
                      defaultMessage: "Login failed",
                      description:
                        "Title for error message when user not found",
                    }),
                    intl.formatMessage({
                      id: "Yvnih+",
                      defaultMessage: "User not found",
                      description:
                        "Description for error message when user not found",
                    }),
                    intl.formatMessage({
                      id: "Hp/8ql",
                      defaultMessage: "Ok close",
                      description: "Button text for error message",
                    }),
                    undefined,
                    true,
                  );
                }

                return;
              }}
            />

            {Platform.OS !== "web" && (
              <>
                <Spacer extraLarge />
                <BodyText colorWhite sizeLarge weightBold>
                  <FormattedMessage
                    id="o6MBsr"
                    defaultMessage="BankID on another device"
                    description="Text for BankID login on another device"
                  />
                </BodyText>
              </>
            )}
          </View>
          {/* End Bottom Sheet */}
        </Layout>
      </Screen>
    );
  }),
  "login", // path where to redirect user after popup is closed
);
